import { useEffect, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import styled from "styled-components";
import { Card, Divider, Typography, Spin, Space } from "antd";
import { PlusOutlined, EuroCircleOutlined } from "@ant-design/icons";

import { ContentWrapper } from "src/components/layout/LayoutComponents";
import { UserApplication, ApplicationStatuses, UserApplicationsResponse } from "@teylor-tools/Api";
import { Axios, ErrorResponse } from "src/utils/Axios";
import ThemedButton from "src/components/themed-ui/ThemedButton";
import ApplicationListItem from "src/pages/dashboard/components/ApplicationListItem";
import { Routes } from "src/Router";
import Error from "src/components/Error";
import { usePageTitle } from "src/hooks/usePageTitle";

const { Title, Text } = Typography;

const SpinWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-grow: 1;
`;

const HeaderWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

export const EmptyView = styled.div`
	background-color: #ffffff;
	padding: 40px 112px 80px;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	@media (max-width: 992px) {
		padding: 32px 72px;
	}

	@media (max-width: 576px) {
		padding: 16px 24px;
	}
`;

const EuroIcon = styled(EuroCircleOutlined)`
	font-size: 24px;
	color: var(--ant-primary-color);
`;

const PageTitle = styled(Title)`
	font-size: 16px !important;
	margin: 0 !important;
`;

const RequiredActionCard = styled(Card)`
	margin-bottom: 25px;

	.ant-card-head-title {
		color: var(--ant-error-color);
	}
`;

const ApplicationList = ({ applications }: { applications: UserApplication[] }) => (
	<>
		{applications.map((application, index) => {
			if (!application.applicationId) return null;
			return (
				<Fragment key={application.applicationId}>
					<ApplicationListItem application={application} />
					{index < applications.length - 1 && <Divider />}
				</Fragment>
			);
		})}
	</>
);

const statusesRequiringAction = [ApplicationStatuses.Open, ApplicationStatuses.Signature];

const DashboardApplicationsList = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<ErrorResponse>();
	const [applications, setApplications] = useState<UserApplication[]>();

	usePageTitle(t("pageTitle.application-list"));

	const completedApplications = applications?.filter(
		(application) => application.status && !statusesRequiringAction.includes(application.status)
	);
	const actionRequiredApplications = applications?.filter(
		(application) => application.status && statusesRequiringAction.includes(application.status)
	);

	useEffect(() => {
		Axios.get<UserApplicationsResponse>("user/applications")
			.then(
				({ data }) => {
					setApplications(data.applications);
				},
				(err: ErrorResponse) => {
					setError(err);
					void Axios.error(err, t("error-messages.dashboard-applications-list-error"));
				}
			)
			.finally(() => setLoading(false));
	}, [t]);

	if (loading)
		return (
			<SpinWrapper>
				<Spin />
			</SpinWrapper>
		);

	if (error) return <Error error={error} />;

	if (!applications?.length)
		return (
			<ContentWrapper>
				<HeaderWrapper>
					<PageTitle>{t("dashboard.my-loans")}</PageTitle>
				</HeaderWrapper>
				<EmptyView>
					<Space direction="vertical" size={24} align="center">
						<Space direction="vertical" align="center">
							<EuroIcon />
							<Title level={5}>{t("dashboard.no-loans-title")}</Title>
							<Text type="secondary">{t("dashboard.no-loans-subtitle")}</Text>
						</Space>

						<ThemedButton
							type="primary"
							size="large"
							icon={<PlusOutlined />}
							onClick={() => {
								navigate(Routes.applicationLoanInfo);
							}}
						>
							{t("dashboard.new-loan")}
						</ThemedButton>
					</Space>
				</EmptyView>
			</ContentWrapper>
		);

	return (
		<ContentWrapper>
			<HeaderWrapper>
				<PageTitle>{t("dashboard.my-loans")}</PageTitle>
				<ThemedButton
					type="ghost"
					icon={<PlusOutlined />}
					onClick={() => {
						navigate(Routes.applicationLoanInfo);
					}}
				>
					{t("dashboard.new-loan")}
				</ThemedButton>
			</HeaderWrapper>

			{!!actionRequiredApplications?.length && (
				<RequiredActionCard title={t("dashboard.action-required")}>
					<ApplicationList applications={actionRequiredApplications} />
				</RequiredActionCard>
			)}

			{!!completedApplications?.length && (
				<Card>
					<ApplicationList applications={completedApplications} />
				</Card>
			)}
		</ContentWrapper>
	);
};

export default DashboardApplicationsList;
